import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'LuxKit/TextLink'
import SolidChartIcon from 'LuxKit/Icons/solid/SolidChartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import Select from 'components/Common/Form/Input/Select'
import { formatOptimizelyTitle, getExperimentCohortKeys, getSelectedCohort } from './ExperimentPanelHelpers'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'

interface ExperimentItemProps {
  experimentId: string
  experiment: App.OptimizelyExperimentDetails
}

export function ExperimentItem({ experimentId, experiment }: ExperimentItemProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const experimentCohorts = useMemo(() => getExperimentCohortKeys(experiment), [experiment])
  const selectedCohort = useMemo(() => getSelectedCohort(experiment, experimentCohorts), [experiment, experimentCohorts])

  const handleCohortChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>((e) => {
    const variationKey = e.target.value
    const enabled = variationKey !== 'off'

    return dispatch(setOptimizelyExperiment(experimentId, {
      ...experiment,
      variationKey,
      enabled,
    }, true))
  }, [experimentId, experiment, dispatch])

  const experimentTitle = useMemo(() => formatOptimizelyTitle(experimentId), [experimentId])
  const reportAvailable = !(experiment.campaignId === undefined || experiment.experimentId === undefined)

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <TextLink
          underline={false}
          href={`https://app.optimizely.com/v2/projects/24799170005/flags/manage/${experimentId}`}
          target="_blank"
          weight="regular"
        >
          {experimentTitle}
        </TextLink>
        <BodyText variant="small" colour="neutral-three">
          {experimentId}
        </BodyText>
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" style={{ width: '100%' }}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText variant="medium" colour="neutral-two">
            <i>{intl.formatMessage({
              id: 'temp_experiment_item_type_label',
              defaultMessage: 'Experiment',
              description: 'Type label shown for an experiment entry in the internal Optimizely control panel',
            })}
            </i>
          </BodyText>
          <IconButton
            kind="tertiary"
            size="small"
            href={`https://app.optimizely.com/v2/projects/24799170005/results/${experiment.campaignId}/experiments/${experiment.experimentId}`}
            target="_blank"
            disabled={!reportAvailable}
          >
            <SolidChartIcon />
          </IconButton>
        </Group>
        <Select
          label=""
          name={`${experimentId}-cohort`}
          value={selectedCohort}
          onChange={handleCohortChange}
          noValidationMessage
          optionalIndicatorEnabled={false}
        >
          {experimentCohorts.map((cohort) => (
            <option value={cohort} key={cohort}>
              {cohort}
            </option>
          ))}
        </Select>
      </Group>
    </Group>
  )
}

export default ExperimentItem
